import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  type DevToolsPlugin,
  type DevToolsContextValue,
  type DevToolsState,
  loadPersistedState,
  persistState,
} from "./types";
import { DevToolsSidebar } from "./DevToolsSidebar";
import { DevToolsDrawer } from "./DevToolsDrawer";

const DevToolsContext = createContext<DevToolsContextValue | null>(null);

export function useDevTools(): DevToolsContextValue {
  const ctx = useContext(DevToolsContext);
  if (!ctx) throw new Error("useDevTools must be used within DevToolsProvider");
  return ctx;
}

interface DevToolsProviderProps {
  plugins: DevToolsPlugin[];
  children?: ReactNode;
}

export function DevToolsProvider({ plugins, children }: DevToolsProviderProps) {
  // Lazy init so the first client render already has the persisted state —
  // no post-mount re-render that would animate from the default (collapsed)
  // to the persisted (open). On the server this returns DEFAULT_STATE.
  // DevToolsSidebar reconciles any leftover SSR className (--collapsed) with
  // React's fiber state in a useLayoutEffect before removing dt-no-anim.
  const [state, setState] = useState<DevToolsState>(loadPersistedState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    persistState(state);
  }, [state]);

  const setActivePlugin = useCallback(
    (id: string | null) => {
      setState((prev) => {
        if (id === null || (id === prev.activePluginId && prev.isDrawerOpen)) {
          return { ...prev, activePluginId: null, isDrawerOpen: false };
        }
        return { ...prev, activePluginId: id, isDrawerOpen: true, isSidebarExpanded: true };
      });
    },
    [],
  );

  const toggleDrawer = useCallback(() => {
    setState((prev) => {
      if (prev.isDrawerOpen) {
        return { ...prev, isDrawerOpen: false };
      }
      const pluginId = prev.activePluginId || plugins[0]?.id || null;
      return { ...prev, isDrawerOpen: true, activePluginId: pluginId, isSidebarExpanded: true };
    });
  }, [plugins]);

  const toggleSidebar = useCallback(() => {
    setState((prev) => {
      if (prev.isSidebarExpanded) {
        return { ...prev, isSidebarExpanded: false, isDrawerOpen: false };
      }
      return { ...prev, isSidebarExpanded: true, activePluginId: null, isDrawerOpen: false };
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        toggleDrawer();
      }
      if (e.key === "Escape" && stateRef.current.isDrawerOpen) {
        setState((prev) => ({ ...prev, isDrawerOpen: false }));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleDrawer]);

  const contextValue: DevToolsContextValue = {
    ...state,
    plugins,
    setActivePlugin,
    toggleDrawer,
    toggleSidebar,
  };

  return (
    <DevToolsContext.Provider value={contextValue}>
      <DevToolsSidebar />
      <DevToolsDrawer />
      {children}
    </DevToolsContext.Provider>
  );
}
